import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NovemberCandidateMatchup from "./NovemberCandidateMatchup";
import NovemberDownBallot from "./NovemberDownBallot";
import {
  NOVEMBER_COUNTIES, NOVEMBER_ELECTION, STATEWIDE_RACES, VOTER_LINKS,
  getDeadlineStatus, getElectionDateKey, getElectionStatus, getNovemberCounty,
} from "../../data/elections/tnNovember32026";

const ExternalLink = ({ href, children, ...props }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
);

const formatReviewDate = (date) => new Date(`${date}T12:00:00Z`).toLocaleDateString("en-US", {
  timeZone: "UTC", month: "long", day: "numeric", year: "numeric",
});

export const NovemberGuide = ({ county, unknownCounty = false, onCountySelect, today }) => {
  const status = getElectionStatus(today);
  const [query, setQuery] = useState("");
  const counties = NOVEMBER_COUNTIES.filter((item) => item.name.toLowerCase().includes(query.trim().toLowerCase()));
  const past = status.phase === "past";

  return (
    <main className="election-page november-page">
      <section className="election-hero november-hero">
        <div className="election-hero-copy">
          <p className="election-kicker">Tennessee • 2026 midterms</p>
          <h1>{county ? `${county.name} November election guide` : "Your guide to the November midterms"}</h1>
          <p>State and federal general election • Tuesday, November 3, 2026</p>
          <p className="november-hero-intro">Know the dates. Review your ballot. Make your voting plan.</p>
          <div className="election-source-actions">
            <a href="#county-resources">{county ? "View county voting resources" : "Find your county"}</a>
            <ExternalLink href={VOTER_LINKS.lookup} className="secondary">Check registration & polling place</ExternalLink>
          </div>
        </div>
        <div className="november-date-card">
          <span>Election Day</span>
          <strong>Nov 3</strong>
          <span>Tuesday, 2026</span>
          <p>Early voting<br /><b>October 14–29</b></p>
        </div>
      </section>

      <section className={`november-status november-status-${status.phase}`} aria-label="Current election status" aria-live="polite">
        <div><h2>{status.heading}</h2><p>{status.detail}</p></div>
        {past && <ExternalLink href={county?.commissionUrl || VOTER_LINKS.elections}>Official election updates →</ExternalLink>}
      </section>

      <section className="november-section" aria-labelledby="dates-heading">
        <div className="election-section-heading"><p>Your voting calendar</p><h2 id="dates-heading">Key dates for November 3</h2></div>
        <div className="november-deadlines">
          <article>
            <span>Voter registration</span><strong>October 5</strong>
            <b>{getDeadlineStatus(NOVEMBER_ELECTION.registrationDeadline, today)}</b>
            <p>{today > NOVEMBER_ELECTION.registrationDeadline ? "The registration deadline for this election has passed. Check your existing registration with GoVoteTN." : "Check your registration and use official registration resources before the deadline."}</p>
          </article>
          <article className="november-deadline-featured">
            <span>Early voting</span><strong>October 14–29</strong>
            <b>{today < NOVEMBER_ELECTION.earlyStart ? "Starts October 14" : today > NOVEMBER_ELECTION.earlyEnd ? "Period ended" : "Early voting period"}</b>
            <p>Check your county’s schedule for locations and daily hours.</p>
          </article>
          <article>
            <span>Absentee ballot request</span><strong>October 24</strong>
            <b>{getDeadlineStatus(NOVEMBER_ELECTION.absenteeRequestDeadline, today)}</b>
            <p>For eligible voters. This is the request deadline; ballot return rules are separate.</p>
            <ExternalLink href={VOTER_LINKS.absentee}>Eligibility & return instructions →</ExternalLink>
          </article>
          <article>
            <span>Election Day</span><strong>November 3</strong>
            <b>{past ? "Election ended" : today === NOVEMBER_ELECTION.date ? "Today — check local hours" : "Tuesday"}</b>
            <p>Use your assigned Election Day polling place. Confirm its hours with your election office.</p>
          </article>
        </div>
        <p className="november-source-note">Dates checked {formatReviewDate(NOVEMBER_ELECTION.checked)} against the <ExternalLink href={NOVEMBER_ELECTION.calendarUrl}>official 2026 election calendar</ExternalLink>. Calendar status uses Central Time; polling hours are local.</p>
      </section>

      <section id="county-resources" className="november-section" aria-labelledby="county-heading">
        <div className="election-section-heading"><p>Local voting information</p><h2 id="county-heading">{county ? `Voting in ${county.name}` : "Find your Tennessee county election guide"}</h2></div>
        {unknownCounty && <p role="alert">That county wasn’t found. Choose a Tennessee county below.</p>}
        <div className="november-county-tools">
          <label htmlFor="november-county">Choose your county</label>
          <select id="november-county" value={county?.slug || ""} onChange={(event) => onCountySelect(event.target.value)}>
            <option value="">Select a county</option>
            {NOVEMBER_COUNTIES.map((item) => <option key={item.slug} value={item.slug}>{item.name}</option>)}
          </select>
        </div>
        {county ? (
          <div className="november-county-resources">
            <p>Use your November sample ballot to check the contests for your address. Countywide sample ballots can include districts and municipal races you cannot vote in.</p>
            <div className="election-source-actions">
              {county.ballotUrl && <ExternalLink href={county.ballotUrl}>November sample ballot</ExternalLink>}
              {county.earlyVotingUrl && <ExternalLink href={county.earlyVotingUrl}>{county.earlyLocationsUrl ? "Early voting daily hours" : "Early voting locations & hours"}</ExternalLink>}
              {county.earlyLocationsUrl && <ExternalLink href={county.earlyLocationsUrl}>Early voting locations</ExternalLink>}
              <ExternalLink href={county.commissionUrl} className="secondary">County election office</ExternalLink>
              <ExternalLink href={VOTER_LINKS.lookup} className="secondary">My ballot & polling place</ExternalLink>
            </div>
            <p className="november-source-note">{county.checked
              ? `November ballot and schedule links checked ${formatReviewDate(county.checked)}. Check the county office for later changes.`
              : county.ballotChecked ? `November ballot checked ${formatReviewDate(county.ballotChecked)}. Confirm early voting locations and daily hours with your county election office.`
              : "A direct November ballot and schedule have not been verified here yet. Use GoVoteTN or contact your county election office for current information."}</p>
            <ExternalLink href={VOTER_LINKS.directory}>State directory of county election offices →</ExternalLink>
          </div>
        ) : <p>Official voter resources for all 95 counties. Select yours for ballot access, early voting information, and your election office.</p>}
        <details className="november-county-directory">
          <summary>Browse all 95 counties</summary>
          <label htmlFor="november-county-search" className="visually-hidden">Search counties</label>
          <input id="november-county-search" type="search" placeholder="Search counties" value={query} onChange={(event) => setQuery(event.target.value)} />
          <div className="election-county-grid">
            {counties.map((item) => <Link key={item.slug} to={`/elections/tn/${item.slug}`} aria-current={county?.slug === item.slug ? "page" : undefined}>{item.name}</Link>)}
          </div>
          {counties.length === 0 && <p role="status">No counties match your search.</p>}
        </details>
      </section>

      <section className="november-section" aria-labelledby="ballot-heading">
        <div className="election-section-heading"><p>On the November ballot</p><h2 id="ballot-heading">Statewide races</h2></div>
        <p>These offices appear statewide. Compare the Republican, Democratic, and independent candidates below, grouped by party with names in ballot order. Vote for one candidate per office; see the official ballot for write-in instructions.</p>
        <div className="november-race-grid">
          {STATEWIDE_RACES.map((race) => (
            <article key={race.id} className="november-race">
              <h3>{race.title}</h3><p>{race.description}</p>
              <NovemberCandidateMatchup race={race} />
              <ExternalLink href={`${race.sourceUrl}#page=${race.sourcePage}`}>Official November ballot • page {race.sourcePage} →</ExternalLink>
            </article>
          ))}
        </div>
        <p className="november-source-note">Statewide candidate lists checked {formatReviewDate(NOVEMBER_ELECTION.checked)} using Davidson County’s official November ballot.</p>
        <div className="november-ballot-notes">
          <article><h3>District & local offices</h3><p>Your address determines your U.S. House, Tennessee House, applicable Tennessee Senate, and local contests. Confirm your current districts and candidates in your official sample ballot.</p><ExternalLink href={VOTER_LINKS.lookup}>Find my ballot →</ExternalLink></article>
          <article><h3>Three constitutional amendments</h3><p>The statewide ballot includes proposals on pretrial bail, a ban on state property taxes, and crime victims’ rights. Read the full text and official summaries before voting.</p><ExternalLink href={VOTER_LINKS.amendments}>Read the proposed amendments →</ExternalLink></article>
          <article><h3>Before you head out</h3><p>Review Tennessee’s voter ID requirements and exceptions, then confirm your location and hours. Early voting sites can differ from your Election Day polling place.</p><ExternalLink href={VOTER_LINKS.photoId}>Voter ID requirements →</ExternalLink></article>
        </div>
      </section>

      <NovemberDownBallot key={county?.slug || "state"} county={county} />

      <footer className="november-footer">
        <p>TN Bill-E is an independent voter guide. Official election offices are the source for ballot changes, schedules, and results.</p>
        <div><Link to={`/elections/tn/august-2026${county ? `/${county.slug}` : ""}`}>August 6 election archive</Link><Link to="/bills">Tennessee bill archive</Link><a href="mailto:legislature@mail.tnbills.com?subject=November%202026%20guide%20correction">Report a correction</a></div>
      </footer>
    </main>
  );
};

const NovemberElectionPage = ({ defaultCounty = null }) => {
  const { countySlug } = useParams();
  const navigate = useNavigate();
  const slug = countySlug || defaultCounty;
  const county = getNovemberCounty(slug);
  const [today, setToday] = useState(() => getElectionDateKey());

  useEffect(() => {
    document.title = `${county ? `${county.name} November 3, 2026 Election Guide` : "Tennessee November 3, 2026 Midterm Election Guide"} | TN Bill-E`;
  }, [county]);

  useEffect(() => {
    const refresh = () => setToday(getElectionDateKey());
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, []);

  return <NovemberGuide county={county} unknownCounty={Boolean(slug && !county)} today={today} onCountySelect={(value) => navigate(value ? `/elections/tn/${value}` : "/elections/tn")} />;
};

export default NovemberElectionPage;
